import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { City } from '../../types';

const CityManagementPanel: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    state: '',
    country: 'India',
    latitude: '',
    longitude: '',
    is_active: true,
    sort_order: 0,
  });

  const [cityToDelete, setCityToDelete] = useState<City | null>(null);

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCities();
      setCities(response.data || []);
    } catch (error: any) {
      console.error('Error loading cities:', error);
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCity = async () => {
    // Validate required fields
    if (!formData.name.trim() || !formData.state.trim()) {
      alert('Please fill in all required fields (City Name and State)');
      return;
    }

    const newCity = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
      longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
      sort_order: cities.length + 1,
    };
    
    try {
      const response = await apiService.createCity(newCity);
      if (response.success && response.data) {
        setCities([...cities, response.data]);
        setFormData({
          name: '',
          state: '',
          country: 'India',
          latitude: '',
          longitude: '',
          is_active: true,
          sort_order: 0,
        });
        setAddDialogOpen(false); // Close the dialog
      }
    } catch (error: any) {
      console.error('Error creating city:', error);
    }
  };

  const handleEditCity = async (city: City) => {
    setEditingCity(city);
    setFormData({
      name: city.name,
      state: city.state,
      country: city.country,
      latitude: city.latitude?.toString() || '',
      longitude: city.longitude?.toString() || '',
      is_active: city.is_active,
      sort_order: city.sort_order || 0,
    });
  };

  const handleUpdateCity = async () => {
    if (!editingCity) return;
    
    // Validate required fields
    if (!formData.name.trim() || !formData.state.trim()) {
      alert('Please fill in all required fields (City Name and State)');
      return;
    }
    
    const updatedCity = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
      longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
    };
    
    try {
      const response = await apiService.updateCity(editingCity.id, updatedCity);
      if (response.success && response.data) {
        setCities(cities.map(c => c.id === editingCity.id ? response.data! : c));
        setEditingCity(null);
        setFormData({
          name: '',
          state: '',
          country: 'India',
          latitude: '',
          longitude: '',
          is_active: true,
          sort_order: 0,
        });
      }
    } catch (error: any) {
      console.error('Error updating city:', error);
    }
  };

  const handleDeleteCity = async () => {
    if (!cityToDelete) return;
    
    try {
      const response = await apiService.deleteCity(cityToDelete.id);
      if (response.success) {
        setCities(cities.filter(city => city.id !== cityToDelete.id));
        setCityToDelete(null);
        setDeleteDialogOpen(false);
      }
    } catch (error: any) {
      console.error('Error deleting city:', error);
    }
  };

  const handleToggleActive = async (city: City) => {
    try {
      const response = await apiService.updateCity(city.id, {
        ...city,
        is_active: !city.is_active,
      });
      if (response.success && response.data) {
        setCities(cities.map(c => c.id === city.id ? response.data! : c));
      }
    } catch (error: any) {
      console.error('Error updating city:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        City Management
      </Typography>
      
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Cities ({cities.length})
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{ minWidth: 120 }}
            >
              Add City
            </Button>
          </Box>
          
          <List sx={{ 
            maxHeight: 400, 
            overflow: 'auto',
            paddingRight: 2, // Increased padding to prevent scrollbar overlap
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Typography>Loading cities...</Typography>
              </Box>
            ) : (
              cities.map((city) => (
                <ListItem
                  key={city.id}
                  sx={{ 
                    paddingRight: 2, // Ensure enough space for action buttons
                    '& .MuiListItem-secondaryAction': {
                      paddingRight: 2, // Extra padding for secondary action area
                    }
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={city.is_active ? 'Active' : 'Inactive'}
                        color={city.is_active ? 'success' : 'default'}
                      />
                      <Switch
                        checked={city.is_active}
                        onChange={() => handleToggleActive(city)}
                        size="small"
                      />
                      <IconButton 
                        edge="end" 
                        onClick={() => handleEditCity(city)}
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        onClick={() => {
                          setCityToDelete(city);
                          setDeleteDialogOpen(true);
                        }}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        {city.name}, {city.state}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        Sort Order: {city.sort_order}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            )}
          </List>
        </CardContent>
      </Card>
      
      {/* Add City Dialog */}
      <Dialog open={addDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Add New City</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="City Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="State"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Country"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              fullWidth
            />
            
            <TextField
              label="Latitude"
              value={formData.latitude}
              onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
              fullWidth
              type="number"
            />
            
            <TextField
              label="Longitude"
              value={formData.longitude}
              onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
              fullWidth
              type="number"
            />
            
            <TextField
              label="Sort Order"
              value={formData.sort_order?.toString() || '0'}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              fullWidth
              type="number"
            />
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Active Status</InputLabel>
              <Select
                value={formData.is_active?.toString() || 'true'}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCity} variant="contained">Add City</Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={editingCity !== null} maxWidth="sm" fullWidth>
        <DialogTitle>Edit City</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="City Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="State"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Country"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              fullWidth
            />
            
            <TextField
              label="Latitude"
              value={formData.latitude}
              onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
              fullWidth
              type="number"
            />
            
            <TextField
              label="Longitude"
              value={formData.longitude}
              onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
              fullWidth
              type="number"
            />
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Active Status</InputLabel>
              <Select
                value={formData.is_active?.toString() || 'true'}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Sort Order"
              value={formData.sort_order?.toString() || '0'}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              fullWidth
              type="number"
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setEditingCity(null)}>Cancel</Button>
          <Button onClick={handleUpdateCity} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Delete City</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{cityToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCity} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CityManagementPanel;
